import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Zap, Eye, EyeOff } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/lib/api";
import { AUTH } from "@/constants/testIds";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      const params = new URLSearchParams(loc.search);
      const next = params.get("next");
      // Only allow same-origin relative paths for safety
      const safeNext = next && next.startsWith("/") && !next.startsWith("//") ? next : null;
      if (safeNext && safeNext.startsWith("/api/sso/")) { window.location.href = safeNext; return; }
      nav(safeNext || loc.state?.from || "/feed");
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || "Login failed");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid={AUTH.loginRoot} className="min-h-[80vh] flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="mb-8 text-center">
          <div className="inline-flex w-14 h-14 rounded-2xl bg-[#00F0FF] items-center justify-center glow-cyan mb-4">
            <Zap className="w-7 h-7 text-black" strokeWidth={3} />
          </div>
          <div className="mono text-[10px] uppercase tracking-[0.3em] text-[#00F0FF] mb-2">// welcome back</div>
          <h1 className="font-heading font-black text-4xl uppercase tracking-tighter">Sign In</h1>
        </div>
        <form onSubmit={submit} className="glass rounded-2xl p-6 space-y-4">
          <Field label="Email" testId={AUTH.emailInput} type="email" value={email} onChange={setEmail} placeholder="you@jixxeo.io" />
          <div className="relative">
            <Field label="Password" testId={AUTH.passwordInput} type={show ? "text" : "password"} value={password} onChange={setPassword} placeholder="••••••••" />
            <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-9 text-white/40 hover:text-white">
              {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          {err && <div data-testid={AUTH.error} className="mono text-xs text-[#FF003C] bg-[#FF003C]/10 border border-[#FF003C]/30 rounded-lg p-3">{err}</div>}
          <button data-testid={AUTH.submit} disabled={busy} className="w-full bg-[#00F0FF] hover:bg-white text-black font-bold mono text-xs uppercase tracking-widest py-3 rounded-full transition-colors disabled:opacity-50">
            {busy ? "Signing in…" : "Sign In"}
          </button>
          <div className="text-center text-sm text-white/50">
            No account?{" "}
            <Link data-testid={AUTH.toggle} to="/register" className="text-[#00F0FF] hover:text-white">Join Jixxeo →</Link>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, testId, type, value, onChange, placeholder }) {
  return (
    <label className="block">
      <div className="mono text-[10px] uppercase tracking-widest text-white/50 mb-1.5">{label}</div>
      <input data-testid={testId} type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} required
        className="w-full bg-white/[0.04] border border-white/10 rounded-lg px-4 py-2.5 focus:outline-none focus:border-[#00F0FF]/60 text-sm" />
    </label>
  );
}
